import Keycloak from "keycloak-js";
import type { KeycloakInstance } from "keycloak-js";
import debug from "debug";
import TokenRefresher from "./TokenRefresher";

const log = debug("app:login");

interface CustomTokenParsed {
  preferred_username?: string;
  realm_access?: {
    roles: string[];
  };
}

type KeycloakConstructor = new (config: {
  url: string;
  realm: string;
  clientId: string;
}) => KeycloakInstance;

const KeycloakTyped = Keycloak as unknown as KeycloakConstructor;

class LoginManager {
  private keycloak!: KeycloakInstance;
  private eventHub: any;
  private initialized = false;
  private authenticated = false;
  private tokenRefresher: TokenRefresher | null = null;

  public ready: Promise<void>;
  private resolveReady!: () => void;

  constructor() {
    this.ready = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
    });
  }

  static instance: LoginManager;

  static getInstance(): LoginManager {
    if (!LoginManager.instance) {
      LoginManager.instance = new LoginManager();
    }
    return LoginManager.instance;
  }

  public async init(eventHub: any): Promise<void> {
    this.eventHub = eventHub;

    this.keycloak = new KeycloakTyped({
      url: "https://admin.xmicrowave.com",
      realm: "X-Microwave",
      clientId: "xmw-layout-frontend",
    });

    // Set up error handler for failed authentication attempts
    this.keycloak.onAuthError = (errorData: any) => {
      log("❌ Keycloak authentication error:", errorData);
      this.eventHub?.emit("activity:failedLogin", {
        details: errorData?.error || "Authentication failed",
        error: errorData?.error || "Unknown error",
      });
    };

    try {
      const authenticated = await this.keycloak.init({
        onLoad: "check-sso",
        checkLoginIframe: false,
        pkceMethod: "S256",
        enableLogging: true,
      } as any);

      this.initialized = true;

      if (authenticated) {
        this.emitLogin();
        this.authenticated = true;

        // Start background token refresher to detect silent session expiry
        this.tokenRefresher = new TokenRefresher(this.keycloak, this.eventHub, this);
        this.tokenRefresher.start();
      } else {
        log("👤 User is a guest");
        this.eventHub?.emit("auth:guest");
        this.resolveReady();
        this.authenticated = false;
      }
    } catch (error: any) {
      log("❌ Keycloak initialization failed:", error);
      // Track failed login if it's an authentication error
      if (error && typeof error === 'object' && ('error' in error || 'error_description' in error)) {
        this.eventHub?.emit("activity:failedLogin", {
          details: error.error_description || error.error || "Initialization failed",
          error: error.error || "Unknown error",
        });
      }
      this.resolveReady(); // Let app continue even if auth fails
    }


    this.eventHub.on("signInClick", () => {
      if(!this.authenticated) {
        this.login();
        return;
      }
      this.logout();
    });



    this.eventHub.on("checkTokenClick", async () => {
      const token = await this.getToken();
      console.log("🔑 Current token:", token ? "Token present" : "No token");
    });

    this.eventHub.on("tokenRequest", async (forceRefresh?: boolean) => {
    log("[LoginManager] Received tokenRequest", forceRefresh ? "(force refresh)" : "");
    try {
      const token = await this.getToken(forceRefresh || false);
      log("[LoginManager] Emitting tokenResponse:", token ? "Token present" : "No token");
      this.eventHub.emit("tokenResponse", token);
    } catch (error) {
      log("[LoginManager] Error getting token:", error);
      this.eventHub.emit("tokenResponse", null);
    }
  });

    // Listen for session expiration events
    this.eventHub.on("sessionExpired", () => {
      log("[LoginManager] Received sessionExpired event");
      this.handleSessionExpiration();
    });

    // Listen for token refresh requests
    this.eventHub.on("tokenRefreshRequest", async () => {
      log("[LoginManager] Received tokenRefreshRequest");
      try {
        const newToken = await this.forceRefreshToken();
        log("[LoginManager] Emitting tokenRefreshResponse:", newToken ? "Token present" : "No token");
        this.eventHub.emit("tokenRefreshResponse", newToken);
      } catch (error) {
        log("[LoginManager] Error refreshing token:", error);
        this.eventHub.emit("tokenRefreshResponse", null);
      }
    });

  }

  private emitLogin(): void {
    const parsed = this.keycloak.tokenParsed as CustomTokenParsed;
    const roles = this.getRoles();

    this.eventHub?.emit("auth:login", {
      roles,
      token: this.keycloak?.token, // Use token directly since we're in login context
    });

    log("🔐 Authenticated as:", parsed?.preferred_username);
    log("🎭 Roles:", roles);
    log("🔐 Token in emitLogin():", this.keycloak.token);

    this.resolveReady();
    this.initialized = true;
  }

  public async getToken(forceRefresh: boolean = false): Promise<string | undefined> {
    if (!this.initialized) {
      log("⚠️ LoginManager not initialized, cannot get token");
      return undefined;
    }
    
    if (!this.authenticated) {
      log("⚠️ User not authenticated, cannot get token");
      return undefined;
    }

    try {
      if (forceRefresh) {
        // Force refresh by using minValidity of 0 (refresh immediately)
        await this.keycloak.updateToken(0);
        log("🔄 Token force refreshed");
      } else {
        // Refresh token if it's about to expire (with 30 seconds buffer)
        await this.refreshToken(30);
      }
      const token = this.keycloak?.token;
      
      if (!token) {
        log("⚠️ Token is null or undefined after refresh");
        return undefined;
      }
      
      return token;
    } catch (error) {
      log("⚠️ Error refreshing/getting token:", error);
      // Return the token anyway if refresh fails, in case it's still valid
      return this.keycloak?.token;
    }
  }

  /** Returns whether the user is currently authenticated (has a valid session). */
  public isAuthenticated(): boolean {
    return this.authenticated;
  }

  public getUsername(): string | undefined {
    const parsed = this.keycloak.tokenParsed as CustomTokenParsed;
    return parsed?.preferred_username;
  }

  public getRoles(): string[] {
    const parsed = this.keycloak.tokenParsed as CustomTokenParsed;
    return parsed?.realm_access?.roles || [];
  }

  public hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  public login(): void {
    this.keycloak.login();
  }

  /**
   * Clears all authentication data from storage and memory
   */
  private clearAllAuthData(): void {
    log("🧹 Clearing all authentication data");
    
    // Clear authenticated state
    this.authenticated = false;
    
    // Clear Keycloak tokens from localStorage and sessionStorage
    // Keycloak stores tokens with various prefixes, clear them all
    this.clearKeycloakStorage();
    
    // Clear any custom authentication-related storage
    this.clearCustomAuthStorage();
    
    // Clear cookies related to authentication
    this.clearAuthCookies();
    
    // Clear in-memory token
    if (this.keycloak) {
      try {
        // Clear Keycloak's internal token state
        this.keycloak.clearToken();
      } catch (error) {
        log("⚠️ Error clearing Keycloak token:", error);
      }
    }
  }

  public logout(): void {
    log("🚪 Logging out user");

    // Mark as not authenticated and clear all auth data immediately in SPA
    this.clearAllAuthData();

    // Stop background token refresher if running
    if (this.tokenRefresher) {
      this.tokenRefresher.stop();
      this.tokenRefresher = null;
    }

    // Call Keycloak logout - let Keycloak handle the redirect and server-side cleanup
    this.keycloak.logout();
  }

  /**
   * Clears all Keycloak-related storage from localStorage and sessionStorage
   */
  private clearKeycloakStorage(): void {
    try {
      // Keycloak typically stores tokens with these patterns:
      // - kc-* (Keycloak prefix)
      // - keycloak-* 
      // - Realm-specific keys
      
      const keycloakPatterns = [
        'kc-',
        'keycloak-',
        'KEYCLOAK_',
        'kc-callback',
        'kc-state',
        'kc-token',
        'kc-refresh-token',
        'kc-id-token',
      ];
      
      // Clear from localStorage
      const localStorageKeys = Object.keys(localStorage);
      localStorageKeys.forEach(key => {
        const shouldClear = keycloakPatterns.some(pattern => 
          key.toLowerCase().includes(pattern.toLowerCase())
        );
        if (shouldClear) {
          log(`🗑️ Clearing localStorage key: ${key}`);
          localStorage.removeItem(key);
        }
      });
      
      // Clear from sessionStorage
      const sessionStorageKeys = Object.keys(sessionStorage);
      sessionStorageKeys.forEach(key => {
        const shouldClear = keycloakPatterns.some(pattern => 
          key.toLowerCase().includes(pattern.toLowerCase())
        );
        if (shouldClear) {
          log(`🗑️ Clearing sessionStorage key: ${key}`);
          sessionStorage.removeItem(key);
        }
      });
      
      // Also clear any keys that might contain the realm name
      const realmName = 'X-Microwave';
      const realmKeys = localStorageKeys.concat(Object.keys(sessionStorage));
      realmKeys.forEach(key => {
        if (key.includes(realmName) || key.includes('x-microwave')) {
          log(`🗑️ Clearing realm-related key: ${key}`);
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        }
      });
      
      log("✅ Keycloak storage cleared");
    } catch (error) {
      log("⚠️ Error clearing Keycloak storage:", error);
    }
  }

  /**
   * Clears any custom authentication-related storage
   */
  private clearCustomAuthStorage(): void {
    try {
      // Clear any custom auth tokens or user data
      const customAuthKeys = [
        'auth_token',
        'access_token',
        'refresh_token',
        'id_token',
        'user_token',
        'api_token',
        'user_data',
        'auth_data',
        'user_authenticated',
      ];
      
      customAuthKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          log(`🗑️ Clearing custom auth localStorage key: ${key}`);
          localStorage.removeItem(key);
        }
        if (sessionStorage.getItem(key)) {
          log(`🗑️ Clearing custom auth sessionStorage key: ${key}`);
          sessionStorage.removeItem(key);
        }
      });
      
      // Also clear window-level auth flags
      if (typeof window !== 'undefined') {
        (window as any).user_authenticated = false;
        (window as any).user_data = null;
        (window as any).auth_token = null;
      }
      
      log("✅ Custom auth storage cleared");
    } catch (error) {
      log("⚠️ Error clearing custom auth storage:", error);
    }
  }

  /**
   * Clears authentication-related cookies
   */
  private clearAuthCookies(): void {
    try {
      // Get all cookies
      const cookies = document.cookie.split(';');
      
      cookies.forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Clear cookies that might be related to authentication
        const authCookiePatterns = [
          'auth',
          'token',
          'session',
          'keycloak',
          'kc-',
        ];
        
        const shouldClear = authCookiePatterns.some(pattern =>
          name.toLowerCase().includes(pattern.toLowerCase())
        );
        
        if (shouldClear) {
          log(`🗑️ Clearing cookie: ${name}`);
          // Clear cookie by setting it to expire in the past
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${window.location.hostname};`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${window.location.hostname};`;
        }
      });
      
      log("✅ Auth cookies cleared");
    } catch (error) {
      log("⚠️ Error clearing auth cookies:", error);
    }
  }

  public async refreshToken(minValidity = 60): Promise<void> {
    if (!this.initialized) return;

    try {
      await this.keycloak.updateToken(minValidity);
      log("🔄 Token refreshed manually.");
    } catch (err: any) {
      log("⚠️ Token refresh failed:", err);

      const rawMessage =
        (err?.error_description ||
          err?.error ||
          err?.message ||
          String(err || "")).toLowerCase();
      const httpStatus = err?.status || err?.httpCode || err?.statusCode;

      // Check if this is a session expiration (refresh token expired or HTTP 400/401).
      const isSessionExpired =
        err?.error === "invalid_grant" ||
        err?.error === "invalid_token" ||
        rawMessage.includes("token expired") ||
        rawMessage.includes("session expired") ||
        rawMessage.includes("refresh token") ||
        rawMessage.includes("invalid_grant") ||
        rawMessage.includes("invalid_token") ||
        httpStatus === 400 ||
        httpStatus === 401;

      if (isSessionExpired && this.authenticated) {
        // Session has expired due to inactivity or server-side logout - auto logout
        log("🔒 Session expired, automatically logging out user");
        this.handleSessionExpiration();
      }
      throw err; // Re-throw so callers know refresh failed
    }
  }

  /**
   * Forces a token refresh and returns the new token.
   * Used when API calls fail with 401 to attempt recovery.
   * @returns The refreshed token, or null if refresh failed
   */
  public async forceRefreshToken(): Promise<string | null> {
    if (!this.initialized || !this.authenticated) {
      log("⚠️ Cannot force refresh token - not initialized or not authenticated");
      return null;
    }

    try {
      // Force refresh by using minValidity of 0 (refresh immediately)
      await this.keycloak.updateToken(0);
      const newToken = this.keycloak?.token;

      if (newToken) {
        log("🔄 Token force refreshed successfully");
        return newToken;
      } else {
        log("⚠️ Token is null after force refresh");
        return null;
      }
    } catch (err: any) {
      log("⚠️ Force token refresh failed:", err);

      const rawMessage =
        (err?.error_description ||
          err?.error ||
          err?.message ||
          String(err || "")).toLowerCase();
      const httpStatus = err?.status || err?.httpCode || err?.statusCode;

      // Check if this is a session expiration (refresh token expired or HTTP 400/401).
      const isSessionExpired =
        err?.error === "invalid_grant" ||
        err?.error === "invalid_token" ||
        rawMessage.includes("token expired") ||
        rawMessage.includes("session expired") ||
        rawMessage.includes("refresh token") ||
        rawMessage.includes("invalid_grant") ||
        rawMessage.includes("invalid_token") ||
        httpStatus === 400 ||
        httpStatus === 401;

      if (isSessionExpired) {
        log("🔒 Session expired during force refresh, logging out user");
        this.handleSessionExpiration();
      }

      return null;
    }
  }

  /**
   * Handles session expiration by showing modal first, then logging out when user acknowledges
   */
  public handleSessionExpiration(): void {
    // Prevent multiple calls
    if (!this.authenticated) {
      log("⚠️ handleSessionExpiration called but user already not authenticated");
      return;
    }

    log("🔒 Handling session expiration - showing modal");

    // Stop background token refresher so it doesn't keep firing
    if (this.tokenRefresher) {
      this.tokenRefresher.stop();
      this.tokenRefresher = null;
    }
    
    // Mark as not authenticated first
    this.authenticated = false;
    
    // Function to perform the actual logout
    const performLogout = () => {
      log("🚪 Performing logout after user acknowledged session expiration");
      
      // Emit events to update UI state
      this.eventHub?.emit("auth:guest");
      this.eventHub?.emit("sign_out", {
        event: "sign_out",
        reason: "session_expired"
      });
      
      // Perform Keycloak logout - simple call, no extra operations to avoid confirmation
      try {
        this.keycloak.logout();
      } catch (error) {
        log("Error during session expiration logout:", error);
        // Even if Keycloak logout fails, refresh the page to clear state
        window.location.reload();
      }
    };
    
    // Show modal notification - logout will happen when user clicks OK or closes modal
    log("📢 Emitting modalInfo event for session expiration");
    
    if (this.eventHub) {
      this.eventHub.emit("modalInfo", {
        title: "You have been logged out",
        message:
          "Your session has expired due to inactivity and you have been logged out. Please sign in again to continue.",
        actions: [
          {
            label: "OK",
            callback: () => {
              log("✅ User clicked OK on session expiration modal");
              performLogout();
            },
          },
        ],
        autoClose: false,
      });
      log("✅ modalInfo event emitted");
      
      // Also listen for modal close (when user clicks X button)
      // Use a MutationObserver to detect when modal is hidden
      const checkModalClose = () => {
        const modalElement = document.querySelector(".global-modal-wrapper");
        if (modalElement && !modalElement.classList.contains("visible")) {
          log("✅ Modal was closed, performing logout");
          performLogout();
          // Clean up observer
          if (observer) {
            observer.disconnect();
          }
        }
      };
      
      // Set up observer to watch for modal close
      const observer = new MutationObserver(() => {
        checkModalClose();
      });
      
      // Start observing after a short delay to ensure modal is rendered
      setTimeout(() => {
        const modalElement = document.querySelector(".global-modal-wrapper");
        if (modalElement) {
          observer.observe(modalElement, {
            attributes: true,
            attributeFilter: ['class']
          });
          log("👀 Started observing modal for close event");
        }
      }, 100);
      
    } else {
      log("❌ eventHub is not available, performing logout immediately");
      performLogout();
    }
  }
}

export default LoginManager;
